import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useMemo } from 'react';
import { Link2, XCircle } from 'lucide-react';
import { AppLayout } from '@/components/layout/AppLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { ProgressDashboard } from '@/components/progress/ProgressDashboard';
import { CompletionView } from '@/components/progress/CompletionView';
import { ProgressNotifications } from '@/components/progress/ProgressNotifications';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorMessage } from '@/components/common/ErrorMessage';
import { Button } from '@/components/ui/button';
import { Toaster } from '@/components/ui/toaster';
import { useToast } from '@/components/ui/use-toast';
import { useMigrationProgress } from '@/hooks/useMigrationProgress';
import { migrationsApi, migrationKeys, modulesApi, moduleKeys } from '@/lib/api';
import { useAuth } from '@/hooks';
import { hasPermission } from '@/types';
import { ForceFailMigrationDialog } from '@/components/migrations/ForceFailMigrationDialog';
import { AttachOrganizationDialog } from '@/components/migrations/AttachOrganizationDialog';
import type { RetryMigrationRequest, ApiError, AttachOrganizationResult } from '@/types';
import type { UserMigrationPhaseState } from '@/hooks/useMigrationProgress';

export const Route = createFileRoute('/migrations/$migrationId')({
  component: MigrationProgressPage,
});

function MigrationProgressPage() {
  const { migrationId } = Route.useParams();
  const queryClient = useQueryClient();
  const { isAuthenticated, user } = useAuth();
  const { toast } = useToast();
  const isAdmin = user?.roles?.some((role) => hasPermission(role, 'admin')) ?? false;
  const [showForceFailDialog, setShowForceFailDialog] = useState(false);
  const [showAttachDialog, setShowAttachDialog] = useState(false);

  // Fetch migration details (only when authenticated)
  const {
    data: migration,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: migrationKeys.detail(migrationId),
    queryFn: () => migrationsApi.get(migrationId),
    enabled: isAuthenticated,
    refetchInterval: (query) => {
      const data = query.state.data;
      // Poll while migration is running OR Phase 2 is actively running or pending
      if (data?.status === 'InProgress') return 5000;
      if (data?.userMigrationPhaseStatus === 'InProgress') return 5000;
      if (data?.userMigrationPhaseStatus === 'Pending') return 5000;
      return false;
    },
  });

  // Fetch module configurations (only when authenticated)
  const { data: modulesConfig = [] } = useQuery({
    queryKey: moduleKeys.all,
    queryFn: () => modulesApi.list(),
    enabled: isAuthenticated,
  });

  const isComplete = migration != null && migration.status !== 'InProgress';

  // Admin recovery when Amplify created the org but its UUID never arrived: only a Failed
  // new-org migration with no org yet can have one attached (the API enforces the same).
  const canAttachOrganization =
    isAdmin &&
    migration?.status === 'Failed' &&
    migration.organizationType === 'New' &&
    !migration.amplifyOrganizationUuid;

  // Fetch persisted logs once migration is complete
  const { data: logsData } = useQuery({
    queryKey: migrationKeys.logs(migrationId),
    queryFn: () => migrationsApi.getLogs(migrationId),
    enabled: isAuthenticated && isComplete,
    staleTime: Infinity,
  });

  const completionLogs = logsData?.items ?? [];

  // Real-time progress via SignalR
  const { progress, logs, isConnected, error: signalRError, userMigrationPhase } = useMigrationProgress(migrationId);

  // Reconcile SignalR phase state against DB truth.
  // If the DB says Phase 2 is terminal but SignalR still shows it as active
  // (e.g. the UserMigrationPhaseCompleted event was missed), reset to idle so
  // the fallback DB-state badge renders instead of a stuck live spinner.
  const reconciledUserMigrationPhase = useMemo((): UserMigrationPhaseState => {
    const terminalDbStatuses = ['Succeeded', 'Partial', 'Failed', 'NotApplicable'];
    if (
      migration &&
      terminalDbStatuses.includes(migration.userMigrationPhaseStatus) &&
      (userMigrationPhase.status === 'onboarding' || userMigrationPhase.status === 'collecting')
    ) {
      return { ...userMigrationPhase, status: 'idle' };
    }
    return userMigrationPhase;
  }, [migration, userMigrationPhase]);

  // Prefer persisted logs (from API) when available; fall back to live SignalR logs.
  const displayLogs = completionLogs.length > 0 ? completionLogs : logs;

  // Force fail mutation
  const { mutate: forceFailMigration, isPending: isForcing } = useMutation({
    mutationFn: () => migrationsApi.forceFail(migrationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: migrationKeys.detail(migrationId) });
      queryClient.invalidateQueries({ queryKey: migrationKeys.lists() });
      setShowForceFailDialog(false);
      toast({ title: 'Migration marked as failed' });
    },
    onError: (error: ApiError) => {
      setShowForceFailDialog(false);
      toast({
        variant: 'destructive',
        title: 'Force fail failed',
        description: error.detail ?? error.title,
      });
    },
  });

  // Attach existing Amplify organization mutation. Errors stay in the dialog so the admin
  // can correct the UUID; the log query sits under the detail key, so the new
  // "Attached existing Amplify organization" entry refreshes with it.
  const {
    mutate: attachOrganization,
    isPending: isAttaching,
    error: attachError,
    reset: resetAttachOrganization,
  } = useMutation<AttachOrganizationResult, ApiError, string>({
    mutationFn: (organizationUuid) =>
      migrationsApi.attachOrganization(migrationId, { organizationUuid }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: migrationKeys.detail(migrationId) });
      setShowAttachDialog(false);
      toast({
        title: 'Organization attached',
        description: `Amplify organization ${result.organizationUuid} is attached. Retry the migration to run its modules.`,
      });
    },
  });

  const openAttachDialog = () => {
    resetAttachOrganization();
    setShowAttachDialog(true);
  };

  // Retry mutation
  const { mutate: retryMigration, isPending: isRetrying } = useMutation({
    mutationFn: (request: RetryMigrationRequest) =>
      migrationsApi.retry(migrationId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: migrationKeys.detail(migrationId) });
    },
  });

  // Retry user migration mutation
  const { mutate: retryUserMigrationMutate, isPending: isRetryingUserMigration } = useMutation({
    mutationFn: () => migrationsApi.retryUserMigration(migrationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: migrationKeys.detail(migrationId) });
      toast({ title: 'User migration retry enqueued' });
    },
    onError: (error: unknown) => {
      const apiError = error as ApiError;
      toast({
        variant: 'destructive',
        title: 'Failed to retry user migration',
        description: apiError.detail ?? apiError.title,
      });
    },
  });


  const handleRetryModule = (moduleKey: string) => {
    retryMigration({ moduleKeys: [moduleKey] });
  };

  const handleRetryAll = () => {
    if (!migration) return;
    const failedModuleKeys = migration.modules
      .filter((m) => m.migrateStatus === 'Failed')
      .map((m) => m.moduleKey);
    retryMigration({ moduleKeys: failedModuleKeys });
  };

  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <LoadingSpinner size="lg" label="Loading migration..." />
        </div>
      </AppLayout>
    );
  }

  if (isError || !migration) {
    return (
      <AppLayout>
        <div className="container max-w-4xl py-8">
          <ErrorMessage
            title="Failed to Load Migration"
            error={error ?? 'Migration not found'}
            onRetry={() => refetch()}
          />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="container max-w-6xl py-8">
        <PageHeader
          title={isComplete ? 'Migration Complete' : 'Migration Progress'}
          description={
            isComplete
              ? `Migration for ${migration.organizationName} has finished`
              : `Monitoring migration for ${migration.organizationName}`
          }
          breadcrumbs={[
            { label: 'Migrations', to: '/' },
            { label: migration.organizationName },
          ]}
        />

        {/* SignalR connection status */}
        {!isComplete && !isConnected && (
          <div className="mb-4 rounded-lg bg-warning/10 p-3 text-sm text-warning-foreground">
            {signalRError
              ? `Real-time updates unavailable: ${signalRError.message}`
              : 'Connecting to real-time updates...'}
          </div>
        )}

        {/* Force Fail action (admin + InProgress only) */}
        {isAdmin && !isComplete && (
          <div className="flex justify-end mb-4">
            <Button
              variant="outline"
              size="sm"
              className="border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
              onClick={() => setShowForceFailDialog(true)}
            >
              <XCircle className="mr-2 h-4 w-4" />
              Force Fail
            </Button>
          </div>
        )}

        <ForceFailMigrationDialog
          open={showForceFailDialog}
          onOpenChange={setShowForceFailDialog}
          migrationName={migration.organizationName}
          onConfirm={() => forceFailMigration()}
          isForcing={isForcing}
        />

        {/* Attach existing Amplify org (admin + Failed new-org migration with no org yet) */}
        {canAttachOrganization && (
          <div className="flex justify-end mb-4">
            <Button variant="outline" size="sm" onClick={openAttachDialog}>
              <Link2 className="mr-2 h-4 w-4" />
              Attach Amplify Organization
            </Button>
          </div>
        )}

        <AttachOrganizationDialog
          open={showAttachDialog}
          onOpenChange={setShowAttachDialog}
          migrationName={migration.organizationName}
          euid={migration.euid}
          onSubmit={(organizationUuid) => attachOrganization(organizationUuid)}
          isAttaching={isAttaching}
          errorMessage={attachError ? (attachError.detail ?? attachError.title) : undefined}
        />

        {/* Main content */}
        {isComplete &&
        (migration.userMigrationPhaseStatus === 'NotApplicable' ||
          migration.userMigrationPhaseStatus === 'Succeeded' ||
          migration.userMigrationPhaseStatus === 'Partial' ||
          migration.userMigrationPhaseStatus === 'Failed') ? (
          <CompletionView
            migration={migration}
            modulesConfig={modulesConfig}
            logs={completionLogs}
            onRetry={handleRetryAll}
            isRetrying={isRetrying}
          />
        ) : (
          <ProgressDashboard
            migration={migration}
            progress={progress}
            logs={displayLogs}
            modulesConfig={modulesConfig}
            onRetryModule={handleRetryModule}
            isRetrying={isRetrying}
            userMigrationPhase={reconciledUserMigrationPhase}
            onRetryUserMigration={retryUserMigrationMutate}
            isRetryingUserMigration={isRetryingUserMigration}
          />
        )}

        {/* Notifications handler */}
        <ProgressNotifications
          migration={migration}
          progress={progress}
          logs={logs}
        />

        {/* Toast container */}
        <Toaster />
      </div>
    </AppLayout>
  );
}
